<template>
  <pling-dialog
      v-model="show"
      :title="title"
      :header_elements_color="'white'"
      :header_background_color="'primary lighten-1'"

      persistent
  >
    <template #content>
      <div class="pa-5 pb-0">
        <!-- Method Selection -->
        <div v-if="step === 'selection'">
          <div class="d-flex align-center mb-4">
            <v-icon large color="warning" class="mr-3">mdi-shield-alert</v-icon>
            <p class="ma-0">
              <template v-if="!canDismissForm">
                {{ $t('2fa.method.register.must_register_message') }}
              </template>
              <template v-else>
                {{ $t('2fa.method.register.message') }}
              </template>
            </p>
          </div>

        <!-- Method Select on Registration  -->


          <template v-if="registerAvailable2FAMethods.length === 0">
            <v-skeleton-loader
                type="list-item-three-line"
                :loading="true"
            />
          </template>

          <two-factor-method-selector
              v-else
              :methods="registerAvailable2FAMethods"
              :title="$t('2fa.method.register.choose_method')"
              subtitle=""
              :show-dismiss-button="canDismissForm"
              :show-back-button="false"
              :auto-select-single="false"
              @method-selected="selectMethod"
              @dismiss="closeRegistrationSelection"
          />
        </div>

        <!-- Method Registration -->
        <two-factor-registration-instructions
            v-if="step === 'registration'"
            :method="twoFactorAuth.selected2FAMethod"
            :instructions-data="instructionsData"
            :can-cancel="canDismissForm || twoFactorAuth.available2FAMethods.length > 1"
            @completed="registerMethodCompleted"
            @cancel="cancelRegistration"
            @title-change="titleChange"
        />
      </div>
    </template>

  </pling-dialog>
</template>

<script>
import {twoFactorAuthStore} from '@/store/pinia/twoFactorAuthStore';
import PlingDialog from '@/components/PlingDialog.vue';
import TwoFactorMethodRegistrationInstructions from '@/components/_security/TwoFactorMethodRegistrationInstructions.vue';
import {sessionStore} from "@/store/pinia/sessionStore";
import TwoFactorRegistrationInstructions from "@/components/_security/TwoFactorMethodRegistrationInstructions.vue";
import {profileStore} from "@/store/pinia/profileStore";
import PlingDialogHeader from "@/components/PlingDialogHeader.vue";
import TwoFactorMethodSelector from "@/components/_security/TwoFactorMethodSelector.vue";

export default {
  name: 'TwoFactorRegistrationForm',
  components: {
    TwoFactorMethodSelector,
    PlingDialogHeader,
    TwoFactorRegistrationInstructions,
    PlingDialog,
    TwoFactorEnrollmentFlow: TwoFactorMethodRegistrationInstructions
  },

  computed: {
    // Backup codes are now a standalone method — fallback auto-prompt removed
    // hasAtLeastOneFallbackEnabled() {
    //   const fallbackMethods = this.twoFactorAuth.availableFallback2FAMethods;
    //   if (!fallbackMethods || fallbackMethods.length === 0) {
    //     return false;
    //   }
    //   return fallbackMethods.some(method => method.is_enabled);
    // }
  },
  setup() {
    const twoFactorAuth = twoFactorAuthStore();
    const session = sessionStore();

    return {
      twoFactorAuth,
      session
    };
  },

  data() {
    return {
      show: true,
      step: 'selection',
      canDismissForm: true,
      dismissTimeout: null,
      registerAvailable2FAMethods: [],
      instructionsData: null,
      main_method: null,
      title: null,
    };
  },
  methods: {

    titleChange(newTitle) {
      this.title = newTitle;
    },

    selectMethod(method) {
      this.twoFactorAuth.selected2FAMethod = method;
      this.registerMethod();
    },

    async registerMethod() {
      if (!this.twoFactorAuth.selected2FAMethod) {
        return;
      }

      try {
        this.step = 'registration';
        const response = await this.twoFactorAuth.register2FAMethod();

        if (!response?.data?.payload) {
          this.backToSelection();
          return response;
        }

        this.instructionsData = response?.data?.payload;

        if (this.main_method) {
          this.instructionsData.main_method = this.main_method;
        }

        return response;
      } catch (error) {
        console.error('Registration failed:', error);
        this.$emit('error', error);
      }
    },

    registerFormCompleted() {

      this.show = false;
      this.$emit('completed');
    },

    async registerMethodCompleted() {
      // If backup codes were just registered, we're fully done
      if (this.twoFactorAuth.selected2FAMethod?.slug === 'backup_codes') {
        this.registerFormCompleted();
        return;
      }

      // After registering a primary method, force backup codes setup if not yet enabled
      const fallbackMethods = this.twoFactorAuth.availableFallback2FAMethods;
      if (!fallbackMethods || fallbackMethods.length <= 0) {
        this.registerFormCompleted();
        return;
      }

      this.main_method = this.twoFactorAuth.selected2FAMethod;
      this.canDismissForm = false;
      const nonEnabledFallbackMethods = fallbackMethods.filter(method => !method.is_enabled);
      if (nonEnabledFallbackMethods.length === 0) {
        this.registerFormCompleted();
        return;
      }

      // Auto-start backup codes registration (only one fallback method expected)
      this.twoFactorAuth.selected2FAMethod = nonEnabledFallbackMethods[0];
      await this.registerMethod();
    },

    backToSelection() {
      this.twoFactorAuth.selected2FAMethod = null;
      this.instructionsData = null;
      this.step = 'selection';
    },

    closeRegistrationSelection() {
      this.show = false;
      this.$emit('dismiss');
    },

    async cancelRegistration() {
      try {
        this.registerAvailable2FAMethods = [...this.twoFactorAuth.available2FAMethods].filter(val => val.slug !== 'backup_codes');
        await this.twoFactorAuth.cancelRegister2FAMethod();
        this.twoFactorAuth.selected2FAMethod = this.main_method;

        if (this.twoFactorAuth.available2FAMethods.length === 1) {
          this.show = false;
          this.$emit('dismiss');
          return;
        }

        this.backToSelection();
      } catch (error) {
        console.error('Cancel failed:', error);
      }
    }
  },

  mounted() {
    this.title = this.$t('2fa.method.register.title');
    this.canDismissForm = this.twoFactorAuth.canDismiss;
    // Single-method auto-registration is handled by TwoFactorMethodSelector emitting method-selected
    // calling registerMethod() here too causes a double API request
  },

  watch: {
    'twoFactorAuth.available2FAMethods': {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          const filteredMethods = newVal.filter(val => !val.is_enabled && val.slug !== 'backup_codes');
          if (filteredMethods.length > 0) {
            this.registerAvailable2FAMethods = filteredMethods;
          }
        }
      },
      immediate: true,
    }
  }

};
</script>
